(ns common.permissions
  (:require [common.graph :as g]
            [common.util :as u]))

(def permission-dependencies
  "Graph describing dependencies between permissions. Keys are `dependants`,
  values are `dependencies`. All dependencies must be `true` in order for the
  `dependant` to be `true`."
  {[:campaigns :create+delete] [[:campaigns :edit]]
   [:campaigns :edit] [[:campaigns :view]]

   [:products :configure] [[:products :edit]]
   [:products :edit] [[:products :view]]

   [:adsets :edit] [[:adsets :view]]
   [:adsets :create+delete] [[:adsets :edit]]
   [:adsets :content] [[:adsets :view]]
   [:adsets :trackers] [[:adsets :view]]
   [:adsets :export] [[:adsets :view]]
   [:adsets :templates] [[:adsets :view]]
   [:adsets :view] [[:campaigns :view]
                    [:templates :view]
                    [:products :view]]

   [:templates :limit] [[:templates :view]]
   [:templates :create+delete] [[:templates :edit]]
   [:templates :edit] [[:templates :view]]})

(def permission-mapping
  "Graph describing how the grouped internal permission representation
   maps to the more granular external representation."
  {[:management :useraccounts] [[:useraccounts :create]
                                [:useraccounts :view]
                                [:useraccounts :edit]
                                [:useraccounts :delete]
                                [:roles :create]
                                [:roles :view]
                                [:roles :edit]
                                [:roles :delete]]

   [:management :adservers] [[:adservers :create]
                             [:adservers :view]
                             [:adservers :edit]
                             [:adservers :delete]]

   [:adsets :content] [[:adsets2 :content]]
   [:adsets :trackers] [[:adsets2 :trackers]]
   [:adsets :export] [[:adsets2 :export]]
   [:adsets :templates] [[:adsets2 :templates]]
   [:adsets :view] [[:adsets2 :view]]
   [:adsets :edit] [[:adsets2 :edit]]
   [:adsets :create+delete] [[:adsets2 :create]
                             [:adsets2 :delete]]

   [:products :view] [[:products :view]]
   [:products :edit] [[:products :edit]]
   [:products :configure] [[:products :configure]]

   [:campaigns :view] [[:campaigns :view]]
   [:campaigns :edit] [[:campaigns :edit]]
   [:campaigns :create+delete] [[:campaigns :create]
                                [:campaigns :delete]]

   [:advertisers :view] [[:advertisers :view]]
   [:advertisers :edit] [[:advertisers :edit]]
   [:advertisers :create+delete] [[:advertisers :create]
                                  [:advertisers :delete]]

   [:templates :view] [[:templates :view]]
   [:templates :edit] [[:templates :edit]]
   [:templates :create+delete] [[:templates :create]
                                [:templates :delete]]})

(def simplified-permission-mapping
  "Like `permission-mapping`, except the grouped internal permission
  representation is even less granular."
  (reduce-kv (fn [m k v]
               (update m (take 1 k) concat v))
             {}
             permission-mapping))

(def permission-limits
  "Graph describing which `limit` affects which permissions."
  {[:templates :limit] [[:templates :view]
                        [:templates :edit]
                        [:templates :delete]]})

(def ^:private group-permissions
  "Group permissions according to `permission-mapping` graph. When merging
  multiple permissions to the same group, the least permissive permission takes
  precedence. Casts `allowed` and `allowed-if-creator` to `true`."
  (partial g/merge-map
           (fn [a b]
             (if (false? a) false (boolean b)))
           permission-mapping))

(def ^:private ungroup-permissions
  "Ungroup permissions according to `permissions-mapping` graph. Casts `true`
  to `allowed`."
  (partial g/merge-map
           (fn [_a b]
             (when b "allowed"))
           (g/invert permission-mapping)))

(def ^:private expand-creator-limits
  "Assign `true` to `limit` fields according to `permission-limits` graph when
  permission equals `allowed-if-creator`."
  (partial g/merge-map
           (fn [a b]
             (or (boolean a) (= b "allowed-if-creator")))
           permission-limits))

(def ^:private condense-creator-limits
  "Assign `allow-if-creator` to permission if `limit` is applicable and
  permission equals `allowed`."
  (partial g/merge-map
           (fn [a b]
             (if (and b (= a "allowed"))
               "allowed-if-creator"
               a))
           (g/invert permission-limits)))

(defn ->internal-representation
  "Transform the external representation to internal representation.
  This is done in 2 passes:
    - Permissions are grouped
    - `limit` booleans are set for `allowed-if-creator` permissions"
  [permissions]
  (-> {}
      (group-permissions permissions)
      (expand-creator-limits permissions)))

(defn ->summarized-internal-representation
  "Group `permissions` by the relations defined in
  `simplified-permission-mapping`. Each group is assigned a number,
  representing full access `2`, partial access `1` and no access `0`."
  [permissions]
  (let [score {"allowed" 2
               "allowed-if-creator" 1
               nil 0}
        strategy (fn [a b]
                   (if (or (nil? a) (= a (score b)))
                     (score b)
                     1))]
    (g/merge-map strategy simplified-permission-mapping {} permissions)))

(defn ->external-representation
  "Ungroup permissions and cast boolean values to `allowed`,
  `allowed-if-creator` or `nil`. Ensures mandetory permissions are enabled."
  [permissions]
  (-> {}
      (ungroup-permissions permissions)
      (condense-creator-limits permissions)
      (assoc-in [:advertisers :view] "allowed")
      (assoc-in [:adservers :view] "allowed")
      (u/filter-nils-map)))

(defn assoc-in-permissions
  "Assoc a `true` or `false` value in a permission, ensuring dependencies are
  updated correctly."
  [permissions ks v]
  (let [graph (cond-> permission-dependencies (not v) g/invert)]
    (reduce (fn [acc ks]
              (assoc-in acc ks v))
            permissions
            (g/depth-first-search graph ks))))
